import { useRuntimeConfig } from '#imports'
import { type MaybeRefOrGetter, toValue } from 'vue'

import type {
  MultipleQueriesQuery,
  SearchResponse,
} from '@algolia/client-search'
import type { Link, Locale } from '@backmarket/http-api'
import algoliasearch from 'algoliasearch'

import {
  type InitializableAlgoliaClient,
  initializeAlgoliaClientWith,
} from './configureAlgoliaSearch'

type ModelsComparisonAlgoliaResponse = {
  results: Array<SearchResponse<ModelsComparisonAlgoliaHit>>
}

type AlgoliaModelLinks = {
  [L in Locale]?: Link
}

type ModelsComparisonAlgoliaHit = {
  model: {
    name: string
    categoryID: number
    nbSales: number
    approved: boolean
    image: string
    links: AlgoliaModelLinks
  }
  objectID: string
}

function toArray<T>(stringOrArray: string | readonly T[]) {
  if (!stringOrArray) return []
  if (typeof stringOrArray === 'string') return [stringOrArray]

  return stringOrArray
}

export const useSearchModelsWithCustomClient =
  (
    initializableAlgoliaClient: InitializableAlgoliaClient<ModelsComparisonAlgoliaResponse>,
  ) =>
  (id: string, apiKey: string) =>
  ({
    selectedModelsNames,
    queries,
  }: {
    selectedModelsNames: MaybeRefOrGetter<string[]>
    queries: MaybeRefOrGetter<MultipleQueriesQuery[]>
  }) => {
    const ignoreSelectedModelsQueriesTransformer = (
      initialQueries: MultipleQueriesQuery[],
    ) => {
      const selectedModelsFacetFilters = toValue(selectedModelsNames).map(
        (modelName) => `model.name:-${modelName}`,
      )
      const ignoreSelectedModelsQueries = initialQueries.map((q) => {
        const queryFaceFiltersArr = toArray(q.params?.facetFilters || [])

        const facetFilters = [
          ...queryFaceFiltersArr,
          ...selectedModelsFacetFilters,
        ]

        return {
          ...q,
          params: {
            ...q.params,
            facetFilters,
          },
        }
      })

      return ignoreSelectedModelsQueries
    }
    const algoliaSearch = initializeAlgoliaClientWith(
      initializableAlgoliaClient,
    )(
      id,
      apiKey,
    )(ignoreSelectedModelsQueriesTransformer)

    async function searchFromText(queryText: string) {
      const initialQueries = toValue(queries)
      const queriesWithQueryText = initialQueries.map((q) => ({
        ...q,
        params: {
          ...q.params,
          query: queryText,
        },
      }))

      const { results } = await algoliaSearch(queriesWithQueryText)

      return results.flatMap((r) =>
        r.hits.map((h) => ({ name: h.model.name, id: h.objectID })),
      )
    }

    return { search: searchFromText }
  }

export function useSearchModelsToCompare(
  config: MaybeRefOrGetter<{
    apiKey: string
    queries: MultipleQueriesQuery[]
  }>,
  selectedModelsNames: MaybeRefOrGetter<string[]>,
) {
  const { ALGOLIA_ID } = useRuntimeConfig().public

  const { apiKey } = toValue(config)

  const initializeAlgoliaClient = useSearchModelsWithCustomClient(
    algoliasearch as InitializableAlgoliaClient<ModelsComparisonAlgoliaResponse>,
  )(ALGOLIA_ID, apiKey)

  const { search } = initializeAlgoliaClient({
    selectedModelsNames,
    queries: () => toValue(config).queries,
  })

  return { searchModels: search }
}
